/**
 * A message you can safely show the user for any failed request.
 *
 * Nearly every catch block in the app read `error.response.data.message`
 * directly. `error.response` only exists when the server actually answered, so
 * the moment the backend was restarted or unreachable that read threw
 * "Cannot read properties of undefined (reading 'data')" from inside a promise
 * catch - which surfaced as an uncaught runtime error and a blank screen,
 * hiding the real problem.
 */
export const apiErrorMessage = (
  error,
  fallback = "Something went wrong. Please try again."
) => {
  const body = error?.response?.data;

  if (typeof body?.message === "string" && body.message.trim()) {
    return body.message.trim();
  }

  // Some endpoints answer with a bare string body.
  if (typeof body === "string" && body.trim()) return body.trim();

  if (typeof body?.error === "string" && body.error.trim()) {
    return body.error.trim();
  }

  // No response at all: the request never reached the server, or was cancelled.
  if (!error?.response) {
    if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
      return "Request cancelled.";
    }
    return "Cannot reach the server. Please check your connection and try again.";
  }

  if (error.response.status === 401) return "Your session has expired. Please sign in again.";
  if (error.response.status === 403) return "You are not permitted to do that.";

  return error?.message || fallback;
};

export default apiErrorMessage;
